// /login — entry to the consumer auth flow.
//
// Visual + copy from
//   design/UI : UX v1.0/for-engineering-auth-admin-consumer/specs/
//   screen-strings.md § 1.
// Form-side widgets are identical to the admin LoginScreen; only the
// surrounding chrome differs (consumer-flavor brand panel via
// <ConsumerAuthShell> instead of <AdminAuthShell>). Per the designer's
// README: "Form-panel headlines/labels/buttons are unchanged between
// portals — only the brand panel + top bar differ."
//
// On submit, calls useAuth().login(); the state machine takes over and the
// AuthRoutes wrapper switches to whatever screen the response dictates.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  LangToggle,
  SsoGoogleIcon,
  SsoMicrosoftIcon,
  TextInput,
} from '@gc-coach/design-system';
import { ConsumerAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

export function LoginScreen() {
  const { t, lang, setLang } = useI18n();
  const { status, error, login, clearError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);

  const submitting = status.kind === 'submitting-login';

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email || !password || submitting) return;
    void login(email, password);
  };

  return (
    <ConsumerAuthShell>
      <div className={styles.topBar}>
        <LangToggle value={lang} onChange={setLang} />
      </div>

      <div className={styles.formColumn}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>{t('login.eyebrow')}</span>
          <h1 className={styles.title}>{t('login.headline')}</h1>
        </header>

        <Card>
          <form onSubmit={onSubmit} className={styles.form} noValidate>
            {error && (
              <Alert severity="danger" onDismiss={clearError}>
                {error.message}
              </Alert>
            )}

            <TextInput
              label={t('login.emailLabel')}
              type="email"
              name="email"
              autoComplete="username"
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              autoFocus
            />

            <TextInput
              label={t('login.passwordLabel')}
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              revealable
              required
            />

            <div className={styles.inlineRow}>
              <Checkbox
                label={t('login.rememberDevice')}
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.currentTarget.checked)}
                disabled={submitting}
              />
              <Link to="/forgot-password" className={styles.inlineLink}>
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button type="submit" kind="brand" fullWidth loading={submitting}>
              {t('login.submitButton')}
            </Button>
          </form>

          <div className={styles.divider}>
            <span className={styles.dividerText}>{t('common.or')}</span>
          </div>

          <div className={styles.ssoStack}>
            <Button
              kind="secondary"
              fullWidth
              leadingIcon={<SsoMicrosoftIcon width={14} height={14} />}
              disabled
            >
              {t('login.ssoMicrosoft')}
            </Button>
            <Button
              kind="secondary"
              fullWidth
              leadingIcon={<SsoGoogleIcon width={14} height={14} />}
              disabled
            >
              {t('login.ssoGoogle')}
            </Button>
            <Button
              kind="secondary"
              fullWidth
              leadingIcon={<span className={styles.ssoBadge}>SSO</span>}
              disabled
            >
              {t('login.ssoEnterprise')}
            </Button>
          </div>
        </Card>

        <p className={styles.footerLine}>
          {t('login.footerLine')}{' '}
          <a href="mailto:onboarding@gc-coach.ai" className={styles.footerLink}>
            {t('login.footerLink')}
          </a>
        </p>
      </div>
    </ConsumerAuthShell>
  );
}
